/**
 * ForceAdvanceDialog
 * ------------------
 * "Not everyone has finished the quiz" — the one place a teacher can move the
 * class on before the quiz gate opens.
 *
 * The gate (lib/classroom/quizGate) holds Next until every student has handed
 * in this topic's quiz. A hard block would make a teacher wait out a student who
 * wandered off or whose headset died, so going early stays possible; it just
 * takes a confirmation that names who is still working and where they are.
 * Nobody loses work by it: moving on hands in each student's answers so far.
 *
 * Shown on flat screens only. In a headset there is no DOM to show it in, and
 * the completed panel's Next takes two presses instead.
 */

import { Loader2, SkipForward } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import type { QuizGate } from '../../lib/classroom/quizGate';

/** Where a student is, in words a teacher reads at a glance. */
const PHASE_LABELS: Record<string, string> = {
  idle: 'not started',
  loading: 'loading',
  intro: 'in the intro',
  explanation: 'in the lesson',
  outro: 'in the summary',
  quiz: 'in the quiz',
  completed: 'on the end screen',
};

interface ForceAdvanceDialogProps {
  open: boolean;
  /** The topic the teacher is trying to move the class to. */
  topicName: string;
  /** The live gate, so the list shrinks as students finish while it is open. */
  gate: Pick<QuizGate, 'done' | 'total' | 'unfinished'>;
  /** A launch is in flight. */
  pending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const ForceAdvanceDialog = ({
  open,
  topicName,
  gate,
  pending = false,
  onCancel,
  onConfirm,
}: ForceAdvanceDialogProps) => {
  // The list is live, so the last student can finish while the teacher reads it.
  const everyoneDone = gate.unfinished.length === 0;
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {everyoneDone ? 'Everyone has now finished the quiz' : 'Not everyone has finished the quiz'}
          </DialogTitle>
          <DialogDescription>
            {everyoneDone
              ? `The whole class has handed in. Move on to "${topicName}"?`
              : `${gate.done} of ${gate.total} students have finished. Their answers so far will be handed in if you move on to "${topicName}".`}
          </DialogDescription>
        </DialogHeader>

        {!everyoneDone && (
          <ul className="max-h-60 overflow-y-auto rounded-lg border border-border">
            {gate.unfinished.map((student) => (
              <li
                key={student.uid}
                className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 text-sm last:border-b-0"
              >
                <span className="truncate font-medium">{student.name}</span>
                <span className="shrink-0 text-xs text-amber-500">
                  {PHASE_LABELS[student.phase] ?? (student.phase || 'connected')}
                </span>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-border px-4 text-sm font-medium transition hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition disabled:opacity-60 ${
              everyoneDone
                ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                : 'bg-amber-500 text-black hover:bg-amber-400'
            }`}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SkipForward className="h-4 w-4" />}
            {everyoneDone ? 'Move on' : 'Move on anyway'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
